import { createBrowserClient } from '@supabase/ssr';
import { buildDocumentCookieString, isValidCookieName } from '@/lib/cookieOptions';
import { getSupabaseConfig } from '@/lib/supabase/config';
import { DEVICE_ID_STORAGE_KEY } from '@/lib/deviceTrust';

const PFX = 'sb_';
const ALT_PFX = 'sb-';
const AUTH_PERSISTENCE_KEY = 'greenpro_auth_persistence';

const getCurrentProjectRef = (): string | null => {
  const { url } = getSupabaseConfig();
  if (!url) return null;
  try {
    const hostname = new URL(url).hostname;
    const ref = hostname.split('.')[0];
    return ref || null;
  } catch {
    return null;
  }
};

const getCurrentProjectAuthTokenMarker = (): string | null => {
  const ref = getCurrentProjectRef();
  return ref ? `${ref}-auth-token` : null;
};

const matchesCurrentProjectToken = (name: string): boolean => {
  const marker = getCurrentProjectAuthTokenMarker();
  if (!marker) return name.includes('auth-token');
  return name.includes(marker);
};

const getAuthPersistenceMode = (): 'persistent' | 'session' => {
  if (typeof window === 'undefined') return 'persistent';
  try {
    const mode = localStorage.getItem(AUTH_PERSISTENCE_KEY);
    return mode === 'session' ? 'session' : 'persistent';
  } catch {
    return 'persistent';
  }
};

export const setAuthPersistenceMode = (remember: boolean) => {
  if (typeof window === 'undefined') return;
  const mode: 'persistent' | 'session' = remember ? 'persistent' : 'session';

  try {
    localStorage.setItem(AUTH_PERSISTENCE_KEY, mode);
  } catch {
    return;
  }

  const source = mode === 'session' ? localStorage : sessionStorage;
  const target = mode === 'session' ? sessionStorage : localStorage;

  try {
    Object.keys(source)
      .filter((k) => k.startsWith(PFX) || k.startsWith(ALT_PFX))
      .forEach((k) => {
        const value = source.getItem(k);
        if (value != null) target.setItem(k, value);
        source.removeItem(k);
      });
  } catch {
    return;
  }
};

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/`;
    cache = document.cookie.includes(`${k}=1`);
    document.cookie = `${k}=; Path=/; Max-Age=0`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const [name, ...rest] = c.trim().split('=');
          return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
        })
        .filter((c) => c.name && matchesCurrentProjectToken(c.name));

const fromStorage = () => {
  try {
    const primary = getAuthPersistenceMode() === 'session' ? sessionStorage : localStorage;
    const secondary = primary === localStorage ? sessionStorage : localStorage;
    const marker = getCurrentProjectAuthTokenMarker();
    const keys = [
      ...Object.keys(primary).filter((k) => k.startsWith(PFX) || k.startsWith(ALT_PFX)),
      ...Object.keys(secondary).filter((k) => k.startsWith(PFX) || k.startsWith(ALT_PFX)),
    ].filter((k) => (!marker ? k.includes('auth-token') : k.includes(marker)));

    return Array.from(new Set(keys)).map((k) => {
      const name = k.startsWith(PFX) ? k.slice(PFX.length) : k.slice(ALT_PFX.length);
      return { name, value: primary.getItem(k) || secondary.getItem(k) || '' };
    });
  } catch {
    return [];
  }
};

const mergeAuthArtifacts = () => {
  const cookieItems = fromCookies();
  const storageItems = fromStorage();
  const merged = new Map<string, string>();

  cookieItems.forEach((item) => {
    if (item?.name) merged.set(item.name, item.value || '');
  });
  storageItems.forEach((item) => {
    if (item?.name && !merged.has(item.name)) {
      merged.set(item.name, item.value || '');
    }
  });

  return Array.from(merged.entries()).map(([name, value]) => ({ name, value }));
};

const clearStorage = () => {
  try {
    [localStorage, sessionStorage].forEach((storage) => {
      Object.keys(storage)
        .filter((k) => k.startsWith(PFX) || k.startsWith(ALT_PFX))
        .forEach((k) => storage.removeItem(k));
    });
  } catch {
    // Ignore localStorage cleanup failures and proceed with cookie cleanup.
  }
  if (typeof document !== 'undefined') {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if ((name.startsWith('sb-') || name.startsWith(PFX)) && isValidCookieName(name)) {
        const expiredCookie = buildDocumentCookieString(
          name,
          '',
          { maxAge: 0 },
          { path: '/', sameSite: 'none', secure: true }
        );

        if (expiredCookie) {
          document.cookie = expiredCookie;
        }
      }
    });
  }
};

const setCookie = (name: string, value: string, options?: any) => {
  const mode = getAuthPersistenceMode();
  const cookieString = buildDocumentCookieString(
    name,
    value,
    {
      ...options,
      maxAge: mode === 'persistent' ? options?.maxAge : undefined,
      expires: mode === 'persistent' ? options?.expires : undefined,
      partitioned: true,
    },
    { path: '/', sameSite: 'none', secure: true, partitioned: true }
  );

  if (cookieString) {
    document.cookie = cookieString;
  }
};

const getToken = () =>
  mergeAuthArtifacts().find((c) => c.name.includes('auth-token'))?.value ?? null;

const getDeviceIdForRequest = () => {
  if (typeof window === 'undefined') return '';

  try {
    return window.localStorage.getItem(DEVICE_ID_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

if (typeof window !== 'undefined' && !(window as any).__sb_patched__) {
  (window as any).__sb_patched__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const token = getToken();
    const deviceId = getDeviceIdForRequest();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    if (url.startsWith('/') || url.startsWith(window.location.origin)) {
      init = {
        ...(init || {}),
        headers: {
          ...(init?.headers || {}),
          ...(token ? { 'x-sb-token': token } : {}),
          ...(deviceId ? { 'x-gp-device-id': deviceId } : {}),
        },
      };
    }
    const response = await orig(input, init);
    if (!response.ok && response.status === 400) {
      try {
        const clone = response.clone();
        const body = await clone.json();
        if (
          body?.error_description?.includes('Refresh Token') ||
          body?.msg?.includes('Refresh Token') ||
          body?.code === 'refresh_token_not_found'
        ) {
          clearStorage();
        }
      } catch {
        // Ignore non-JSON error bodies.
      }
    }
    return response;
  };
}

// Singleton instance — prevents multiple clients competing for Web Locks
let _clientInstance: ReturnType<typeof createBrowserClient> | null = null;

export function createClient() {
  if (typeof window !== 'undefined' && _clientInstance) {
    return _clientInstance;
  }

  const { url, anonKey } = getSupabaseConfig();

  if (!url || !anonKey) {
    throw new Error('NEXT_PUBLIC_SUPABASE_URL e NEXT_PUBLIC_SUPABASE_ANON_KEY precisam estar configuradas.');
  }

  const client = createBrowserClient(
    url,
    anonKey,

    {
      auth: {
        flowType: 'pkce',
      },
      cookies: {
        getAll: () => mergeAuthArtifacts(),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value
                ? setCookie(name, value, options)
                : (() => {
                    const expiredCookie = buildDocumentCookieString(
                      name,
                      '',
                      { ...options, maxAge: 0, expires: new Date(0), partitioned: true },
                      { path: '/', sameSite: 'none', secure: true, partitioned: true }
                    );

                    if (expiredCookie) {
                      document.cookie = expiredCookie;
                    }
                  })()
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              if (!isValidCookieName(name)) {
                return;
              }

              const storage =
                getAuthPersistenceMode() === 'session'
                  ? window.sessionStorage
                  : window.localStorage;
              try {
                if (value) {
                  storage.setItem(`${PFX}${name}`, value);
                } else {
                  storage.removeItem(`${PFX}${name}`);
                }
              } catch {
                // Keep cookie-based auth flow even if localStorage write fails.
              }
              if (value) setCookie(name, value, options);
            });
          }
        },
      },
    }
  );

  if (typeof window !== 'undefined') {
    _clientInstance = client;
  }

  return client;
}
